import { useEffect, useState } from 'react';
import { booleanPointInPolygon } from '@turf/boolean-point-in-polygon';
import { WALES_BOUNDARY } from './walesBoundary';
import { SCOTLAND_BOUNDARY } from './scotlandBoundary';

export const IRELAND_EXCLUSION = { west: -10.6, east: -5.0, south: 51.3, north: 55.5 };

// Region extents used to classify the viewport centre into a covered country
// (drives layer availability and the legend). The out-of-coverage state is what
// swaps the panel over to the "Return to zone" action in MapLayerPanel.
//
// BBB-517: neither of England's land borders is axis-aligned, so no set of
// rectangles can separate the three countries — the England bbox fully contains
// Wales's, and the Scotland bbox reaches ~0.38 deg further south than the real
// border. The bboxes remain the cheap first-pass filter, but a hit on wales or
// scotland is confirmed against a real boundary polygon before it is returned.
// Which places were misclassified, and why the polygons are shaped as they are:
// docs/boundary-classification.md.
//
// Exported for the invariant tests: two things in classifyPoint below are only
// correct because of how these rectangles overlap, so the overlaps are asserted
// rather than left as prose.
export const BBOXES = {
  scotland: { west: -7.65, east: -0.7, south: 54.6, north: 60.9 },
  wales: { west: -5.35, east: -2.65, south: 51.3, north: 53.45 },
  england: { west: -6.4, east: 2.1, south: 49.8, north: 55.85 },
};

// Every region the map layers cover, in the order classifyPoint tries them.
//
// The order is load-bearing: England is last because its bbox contains Wales
// entirely and overlaps Scotland, so it is the fallback the other two steal
// from. Do not reorder without reading classifyPoint below.
//
// Exported because MapLayerPanel gates its panel on the same set, and a second
// hand-typed copy there drifts the moment a region is added. Deriving it is
// exact rather than convenient: classifyPoint can only ever return a member of
// this list or the out-of-coverage sentinel, so "a region the panel serves" and
// "a region the classifier names" are the same set by construction.
//
// The BBOXES keys are asserted to match this list in useViewportRegion.test.js,
// so adding one without the other fails loudly instead of silently never being
// classified.
export const COVERED_REGIONS = ['scotland', 'wales', 'england'];

function inBbox(lng, lat, bbox) {
  return lng >= bbox.west && lng <= bbox.east && lat >= bbox.south && lat <= bbox.north;
}

// Precise checks used only to arbitrate bbox overlaps with England. See
// walesBoundary.js / scotlandBoundary.js for data source, licence and
// accuracy notes.
function isActuallyInWales(lng, lat) {
  return booleanPointInPolygon([lng, lat], WALES_BOUNDARY);
}

function isActuallyInScotland(lng, lat) {
  return booleanPointInPolygon([lng, lat], SCOTLAND_BOUNDARY);
}

function readMapCentre(map) {
  const centre = map.getCenter();
  if (!centre) return null;

  return {
    lat: typeof centre.lat === 'function' ? centre.lat() : centre.lat,
    lng: typeof centre.lng === 'function' ? centre.lng() : centre.lng,
  };
}

export function classifyPoint(lng, lat) {
  // The Ireland box overlaps real UK land — west Pembrokeshire (St Davids), the
  // Rhins of Galloway (Stranraer) and Kintyre all sit inside it. Returning the
  // sentinel on a bbox hit alone therefore stole those places out of coverage,
  // which is the same failure the country bboxes had. Confirm against the
  // polygons before excluding. Both are already loaded, and this only runs for
  // points in the box at all, so it costs nothing in normal use.
  if (
    inBbox(lng, lat, IRELAND_EXCLUSION) &&
    !isActuallyInWales(lng, lat) &&
    !isActuallyInScotland(lng, lat)
  ) {
    return 'out-of-coverage';
  }

  // Only points that could also be England need arbitrating — England is the
  // lowest-priority region, so it is the one the other two can steal from.
  const contested = inBbox(lng, lat, BBOXES.england);

  for (const region of COVERED_REGIONS) {
    if (!inBbox(lng, lat, BBOXES[region])) continue;
    // A hit must be confirmed by the country's real boundary; failing that,
    // fall through and let England's bbox pick it up.
    //
    // Wales needs no `contested` guard: its bbox sits entirely inside England's,
    // so a Wales hit is always contested. Asserted in useViewportRegion.test.js.
    if (region === 'wales' && !isActuallyInWales(lng, lat)) continue;
    // Scotland does. SCOTLAND_BOUNDARY is clipped to the overlap corridor, so it
    // answers false for Inverness, Aberdeen and everything north of 55.95;
    // consulting it outside the corridor would send most of Scotland
    // out-of-coverage. Do not drop this gate.
    if (contested && region === 'scotland' && !isActuallyInScotland(lng, lat)) continue;
    return region;
  }

  return 'out-of-coverage';
}

export function isOutOfCoverage(region) {
  return region === 'out-of-coverage';
}

export function useViewportRegion(map) {
  const [region, setRegion] = useState(() => {
    if (!map) return 'england';
    const centre = readMapCentre(map);
    return centre ? classifyPoint(centre.lng, centre.lat) : 'england';
  });

  useEffect(() => {
    if (!map) return undefined;

    const classify = () => {
      const centre = readMapCentre(map);
      if (centre) setRegion(classifyPoint(centre.lng, centre.lat));
    };

    queueMicrotask(classify);
    const listener = map.addListener('idle', classify);

    return () => {
      listener.remove();
    };
  }, [map]);

  return region;
}
